import { Component, Input, OnChanges, OnInit, type SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// import { NouiFormatter, NouisliderComponent } from 'nouislider';
import { Options } from '@angular-slider/ngx-slider';
import {
  DEFAULT_FLIGHT_FILTERS,
  FlightEnterpriseSearchInterface,
  FlightFilters,
  FlightSortTypes,
  FlightDisplayTypes,
  FlightSearchType
} from '@sabstravtech/obtservices/base';
import {
  ServiceType,
  EnterpriseSearchService,
  WithSubscriptionComponent,
  UserService
} from '@sabstravtech/obtservices/angular';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Helpers } from '../../../../core/classes/helpers';
import { LightningUserFavorurite } from '../../../../core/classes/user-favourite.enum';

interface RangeFilterValue {
  value: number;
  highValue: number;
}

interface FilterValue {
  key: string;
  title: string;
  checked: boolean;
}

export enum negotiatedRatesKeys {
  CLIENT = 'Corporate Fare'
}

@Component({
  selector: 'app-filter-flights',
  templateUrl: './filter-flights.component.html',
  styleUrls: ['./filter-flights.component.scss'],
  standalone: false
})
export class FilterFlightsComponent extends WithSubscriptionComponent implements OnInit, OnChanges {
  @Input() filters: FlightFilters;
  @Input() display: FlightDisplayTypes;
  public ourFilters: FlightFilters = DEFAULT_FLIGHT_FILTERS;
  public priceOptions: Options = { floor: 0, ceil: 0 };
  public depTimeOptions: Options = { floor: 0, ceil: 0 };
  public arrTimeOptions: Options = { floor: 0, ceil: 0 };
  public inboundDepTimeOptions: Options = { floor: 0, ceil: 0 };
  public inboundArrTimeOptions: Options = { floor: 0, ceil: 0 };

  public sortType = FlightSortTypes.Cheapest;
  public searchParams: FlightEnterpriseSearchInterface;
  public FlightSearchType = FlightSearchType;
  public selectableFilters;
  public standardFilters = [
    {
      title: 'Preferred',
      sortType: FlightSortTypes.Preferred,
      icon: 'ico-heart input_icon',
      ariaLabel: 'Sort by Preferred'
    },
    {
      title: 'Price',
      sortType: FlightSortTypes.Cheapest,
      icon: 'ico-gbp',
      ariaLabel: 'Sort by Price'
    },
    {
      title: 'Lowest Emissions',
      sortType: FlightSortTypes.LowestEmissions,
      icon: 'ico-co2 input_icon',
      ariaLabel: 'Sort by Co2 Emissions'
    },
    {
      title: 'Quickest',
      sortType: FlightSortTypes.Quickest,
      icon: 'ico-clock input_icon',
      ariaLabel: 'Sort by Quickest'
    },
    {
      title: 'Most Direct',
      sortType: FlightSortTypes.MostDirect,
      icon: 'ico-chevron-right input_icon',
      ariaLabel: 'Sort by Most Direct'
    },
    {
      title: 'Departure Time',
      sortType: FlightSortTypes.DepartureTime,
      icon: 'ico-icon-takeoff input_icon',
      ariaLabel: 'Sort by Departure Time'
    },
    {
      title: 'Arrival Time',
      sortType: FlightSortTypes.ArrivalTime,
      icon: 'ico-icon-landing input_icon',
      ariaLabel: 'Sort by Arrival Time'
    }
  ];

  public active = 1;

  public $triggerApplyFilter = new Subject<FlightFilters>();

  public viaShowmore = false;

  /** Remembered chatbot deep-link flight numbers (positional outbound,inbound)
   *  parsed from the `flightNumberFilter` query param. */
  private chatbotFlightNumber = '';
  private chatbotInboundFlightNumber = '';

  constructor(
    public searchService: EnterpriseSearchService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super();
    this.searchParams = this.searchService.searches[ServiceType.Flight];
  }

  ngOnInit() {
    if (this.searchParams.bySchedule) {
      const priceFilterIndex = this.standardFilters.findIndex((filter) => filter.title === 'Price');
      if (priceFilterIndex !== -1) {
        this.standardFilters.splice(priceFilterIndex, 1);
      }
    }
    this.selectableFilters = this.standardFilters;
    const defaultSortType = FlightSortTypes.Preferred;
    this.sortType =
      this.userService.getUserFavoriteObject<{ sortOrder: FlightSortTypes }>(
        LightningUserFavorurite.FlightResultsConfiguration
      )?.sortOrder || defaultSortType;
    this.setSortby(this.sortType);

    this.subscribe(this.$triggerApplyFilter.pipe(debounceTime(200)), (filters: FlightFilters) => {
      this.searchParams.applyFilters(filters, this.searchParams.currentSort);
    });

    // ENT-16220 — when the chatbot deep-links here after a fare pick it carries
    // `?flightNumberFilter=out,in`. Subscribe reactively (not a one-shot
    // snapshot) so it also works when the user is already on this page and
    // Angular reuses the component — only `queryParams` re-emits, ngOnInit does
    // not re-run. Mirrors the hotel card's `hotelNameFilter` behaviour.
    this.subscribe(this.route.queryParams, (params) => {
      this.applyChatbotFlightNumberFilter(params?.['flightNumberFilter']);
    });

    // ENT-16220 — when a brand-new search starts, drop any stale chatbot
    // deep-link filter and scrub `flightNumberFilter` out of the URL (mirrors
    // the hotel page clearing `hotelNameFilter` on isLoading).
    this.subscribe(this.searchParams.isLoading, (loading: boolean) => {
      if (loading && (this.chatbotFlightNumber || this.chatbotInboundFlightNumber)) {
        this.chatbotFlightNumber = '';
        this.chatbotInboundFlightNumber = '';
        this.mergeChatbotFlightNumberFilter();
        this.router.navigate([], {
          queryParams: { flightNumberFilter: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
    if (this.ourFilters?.privateFares && Object.keys(this.ourFilters.privateFares).length) {
      // If private fares available, AND default filter UI config selected, select filter
      const defaultFilters = this.userService.getUserFavoriteObject<{
        defaultFilters: { negotiatedRatesClient: boolean };
      }>(LightningUserFavorurite.FlightResultsConfiguration)?.defaultFilters;
      if (
        defaultFilters.negotiatedRatesClient &&
        this.ourFilters.privateFares[negotiatedRatesKeys.CLIENT]
      ) {
        this.ourFilters.privateFares[negotiatedRatesKeys.CLIENT].selected = true;
        this.applyFilter(this.ourFilters);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters']) {
      this.ourFilters = this.clearFlightFilters(this.filters);
      // Re-apply any active chatbot deep-link filter — clearFlightFilters()
      // rebuilds ourFilters from the freshly-recomputed search filters (which
      // drop flightNumber), so without this a results refresh would lose it.
      this.mergeChatbotFlightNumberFilter();
      if (this.searchParams.bySchedule || changes.display) {
        const defaultSortType = this.searchParams.bySchedule
          ? FlightSortTypes.Quickest
          : FlightSortTypes.Cheapest;
        this.sortType =
          this.userService.getUserFavoriteObject<{ sortOrder: FlightSortTypes }>(
            LightningUserFavorurite.FlightResultsConfiguration
          )?.sortOrder || defaultSortType;
        this.searchParams.applyFilters(this.filters, this.searchParams.currentSort);
      }
      if (this.chatbotFlightNumber || this.chatbotInboundFlightNumber) {
        this.applyFilter(this.ourFilters);
      }
    }
  }

  clear(): void {
    // Also drop any chatbot deep-link filter so "Clear" reveals every flight,
    // and scrub it from the URL so a later results refresh can't re-stick it.
    if (this.chatbotFlightNumber || this.chatbotInboundFlightNumber) {
      this.chatbotFlightNumber = '';
      this.chatbotInboundFlightNumber = '';
      this.router.navigate([], {
        queryParams: { flightNumberFilter: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
    this.ourFilters = this.clearFlightFilters(this.filters);
    this.applyFilter(this.ourFilters);
  }

  clearFlightFilters(original: FlightFilters): FlightFilters {
    if (!original) {
      return DEFAULT_FLIGHT_FILTERS;
    }
    const filters = Helpers.clone(original);
    this.priceOptions = {
      floor: Math.floor(filters.price.min === Number.MAX_SAFE_INTEGER ? 0 : filters.price.min),
      ceil: Math.ceil(filters.price.max === Number.MIN_SAFE_INTEGER ? 0 : filters.price.max),
      step: 1,
      ariaLabel: 'Minimum price in pounds',
      ariaLabelHigh: 'Maximum price in pounds'
    };
    filters.price.min = this.priceOptions.floor;
    filters.price.max = this.priceOptions.ceil;
    this.depTimeOptions = {
      floor: filters.departureTime.min,
      ceil: filters.departureTime.max,
      ariaLabel: 'Minimum outbound departure time',
      ariaLabelHigh: 'Maximum outbound departure time'
    };
    this.arrTimeOptions = {
      floor: filters.arrivalTime.min,
      ceil: filters.arrivalTime.max,
      ariaLabel: 'Minimum outbound arrival time',
      ariaLabelHigh: 'Maximum outbound arrival time'
    };
    this.inboundDepTimeOptions = {
      floor: filters.departureTime.min,
      ceil: filters.departureTime.max,
      ariaLabel: 'Minimum inbound departure time',
      ariaLabelHigh: 'Maximum inbound departure time'
    };
    this.inboundArrTimeOptions = {
      floor: filters.arrivalTime.min,
      ceil: filters.arrivalTime.max,
      ariaLabel: 'Minimum inbound arrival time',
      ariaLabelHigh: 'Maximum inbound arrival time'
    };

    return filters;
  }

  applyFilter(filters: FlightFilters) {
    this.$triggerApplyFilter.next(filters);
  }

  /**
   * When the chatbot navigates the user here after picking a flight card it
   * passes `?flightNumberFilter=BA123,BA456` — positional "outbound,inbound"
   * tokens (either side may be empty, e.g. `,BA456` for a dual-single inbound
   * pick). We remember the requested numbers, merge them onto `ourFilters`
   * (which already carries the live search's valid price/time ranges) and
   * re-apply. When the param is absent (e.g. a brand-new search navigated here
   * without it) we clear the deep-link so it doesn't stick — matching the way
   * the hotel card's name filter clears on a new search.
   */
  private applyChatbotFlightNumberFilter(raw: string | undefined): void {
    const tokens = (raw ?? '').split(',').map((t) => t.trim());
    this.chatbotFlightNumber = tokens[0] ?? '';
    this.chatbotInboundFlightNumber = tokens[1] ?? '';
    this.mergeChatbotFlightNumberFilter();
    if (this.chatbotFlightNumber || this.chatbotInboundFlightNumber) {
      this.applyFilter(this.ourFilters);
    }
  }

  /** Push the remembered chatbot flight numbers onto both `ourFilters` and the
   *  shared `searchParams.filters` source, without disturbing valid filter
   *  ranges. Safe to call repeatedly.
   *
   *  The shared source matters because the flight results page renders BOTH
   *  this component and `app-flight-sort-by`, and each independently clones
   *  `searchParams.filters` and calls `applyFilters`. If only our local
   *  `ourFilters` carried the number, a sort-driven `applyFilters`. */
  private mergeChatbotFlightNumberFilter(): void {
    const shared = this.searchParams?.filters?.value;
    if (shared && !Object.isFrozen(shared)) {
      shared.flightNumber = this.chatbotFlightNumber;
      shared.inboundFlightNumber = this.chatbotInboundFlightNumber;
    }
    if (!this.ourFilters || Object.isFrozen(this.ourFilters)) {
      return;
    }
    this.ourFilters.flightNumber = this.chatbotFlightNumber;
    this.ourFilters.inboundFlightNumber = this.chatbotInboundFlightNumber;
  }

  moveArrowDown(e): void {
    e.preventDefault(); // Stops page scrolling
    (<HTMLElement>document.activeElement.nextElementSibling).focus();
  }
  moveArrowUp(e): void {
    e.preventDefault(); // Stops page scrolling
    (<HTMLElement>document.activeElement.previousElementSibling).focus();
  }
  setSortby(sortType: FlightSortTypes): void {
    this.sortType = sortType;
    this.applyFilter(this.ourFilters);
  }
}
